import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BRAND, PRODUCTS } from "../data/products";
import {
  ArrowUpRight,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Youtube,
} from "lucide-react";
import Logo from "./Logo";

/* =====================================================================
   Footer — LIFTILE premium corporate footer.
   • Large animated logo (subtle orange glow, breathe + hover lift)
   • Quick Links · Products · Contact · Social
   • "Made in India" tricolor badge + "ISO 9001:2015 CERTIFIED" badge
   All inside the existing dark-theme site DNA. Only the footer file
   was touched — no other section modified.
   ===================================================================== */

const QUICK_LINKS = [
  ["Home", "/"],
  ["About Us", "/about"],
  ["Products", "/products"],
  ["Projects", "/projects"],
  ["Dealer", "/dealer"],
  ["Insights", "/insights"],
  ["Contact Us", "/contact"],
];

const SOCIALS = [
  { Icon: Facebook,  label: "facebook",  href: "#" },
  { Icon: Instagram, label: "instagram", href: "#" },
  { Icon: Linkedin,  label: "linkedin",  href: "#" },
  { Icon: Youtube,   label: "youtube",   href: "#" },
];

/* ---------- Made in India Badge (inline SVG — reuses no external asset) ---------- */
function MadeInIndiaBadge() {
  return (
    <div
      data-testid="footer-made-in-india"
      className="group inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-liftile-orange/40 hover:bg-white/[0.04] transition-all duration-400"
    >
      {/* Circular tricolor + Ashoka Chakra emblem */}
      <span
        aria-hidden
        className="relative flex items-center justify-center w-9 h-9 rounded-full overflow-hidden shadow-[0_4px_14px_-4px_rgba(0,0,0,0.5)]"
      >
        <svg viewBox="0 0 36 36" className="w-full h-full">
          {/* Saffron */}
          <rect x="0" y="0"  width="36" height="12" fill="#FF9933" />
          {/* White */}
          <rect x="0" y="12" width="36" height="12" fill="#FFFFFF" />
          {/* Green */}
          <rect x="0" y="24" width="36" height="12" fill="#138808" />
          {/* Ashoka Chakra (24-spoke) */}
          <g transform="translate(18 18)" stroke="#000080" strokeWidth="0.6" fill="none">
            <circle r="4.6" strokeWidth="0.9" />
            <circle r="0.7" fill="#000080" stroke="none" />
            {Array.from({ length: 24 }).map((_, i) => {
              const a = (i * Math.PI) / 12;
              return (
                <line
                  key={i}
                  x1={Math.cos(a) * 0.9}
                  y1={Math.sin(a) * 0.9}
                  x2={Math.cos(a) * 4.6}
                  y2={Math.sin(a) * 4.6}
                />
              );
            })}
          </g>
        </svg>
      </span>
      <span className="flex flex-col leading-tight">
        <span className="font-heading text-[10px] uppercase tracking-[0.22em] text-liftile-cream/55 font-semibold">
          Proudly
        </span>
        <span className="font-heading text-[13px] uppercase tracking-[0.14em] text-liftile-cream font-bold">
          Made in India
        </span>
      </span>
    </div>
  );
}

/* ---------- ISO 9001:2015 Certified Badge (inline SVG) ---------- */
function ISOCertifiedBadge() {
  return (
    <div
      data-testid="footer-iso-certified"
      className="group inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-liftile-orange/40 hover:bg-white/[0.04] transition-all duration-400"
    >
      {/* Shield seal */}
      <span
        aria-hidden
        className="relative flex items-center justify-center w-9 h-9 rounded-full shadow-[0_4px_14px_-4px_rgba(249,115,22,0.4)]"
        style={{
          background:
            "linear-gradient(140deg, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.06) 100%)",
          border: "1px solid rgba(249,115,22,0.45)",
        }}
      >
        <ShieldCheck className="w-4 h-4 text-liftile-orange" strokeWidth={2.4} />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="font-heading text-[10px] uppercase tracking-[0.22em] text-liftile-cream/55 font-semibold">
          ISO
        </span>
        <span className="font-heading text-[13px] uppercase tracking-[0.14em] text-liftile-cream font-bold">
          9001:2015 Certified
        </span>
      </span>
    </div>
  );
}

/* ---------- Section heading ---------- */
function ColumnHeading({ children }) {
  return (
    <h4 className="relative font-heading text-[11px] uppercase tracking-[0.28em] text-liftile-cream mb-6 pb-3 font-bold">
      {children}
      <span
        aria-hidden
        className="absolute left-0 bottom-0 w-9 h-[2px] bg-liftile-orange rounded-full"
      />
    </h4>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="footer"
      className="relative overflow-hidden bg-liftile-black border-t border-white/[0.06]"
      style={{ backgroundColor: "#070708" }}
    >
      {/* Subtle brand DNA — top orange hairline + top-left ambient glow */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-liftile-orange/25" />
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* ═══════════ Main grid ═══════════ */}
      <div className="container-x relative pt-20 pb-14 md:pt-24 md:pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
        {/* ─── LOGO + tagline + socials (spans 4 cols on lg) ─── */}
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative inline-block"
          >
            {/* Ambient orange glow behind the logo */}
            <span
              aria-hidden
              className="absolute inset-0 -m-6 rounded-3xl pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(60% 60% at 30% 50%, rgba(249,115,22,0.28) 0%, rgba(249,115,22,0.08) 45%, transparent 70%)",
                filter: "blur(22px)",
                animation: "footer-logo-breathe 6s ease-in-out infinite",
                willChange: "opacity",
              }}
            />
            <Link
              to="/"
              aria-label="LIFTILE — Home"
              data-testid="footer-logo-link"
              className="relative inline-block transition-transform duration-500 hover:-translate-y-1"
            >
              <Logo
                variant="full"
                theme="light"
                className="relative h-28 md:h-32 lg:h-36 w-auto drop-shadow-[0_10px_30px_rgba(249,115,22,0.25)]"
              />
            </Link>
          </motion.div>

          <p className="font-body text-[13.5px] md:text-sm text-liftile-cream/65 mt-7 leading-relaxed max-w-md">
            India&apos;s trusted manufacturer of construction lifting equipment —
            engineered for safety, reliability and maximum on-site performance.
            Owner-inspected before every dispatch.
          </p>

          {/* Follow Us cluster (label + icons) */}
          <div className="mt-9">
            <h4 className="font-heading text-[10.5px] uppercase tracking-[0.28em] text-liftile-cream/70 font-semibold mb-4">
              Follow Us
            </h4>
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={`LIFTILE on ${label}`}
                  data-testid={`footer-social-${label}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-10 h-10 grid place-items-center rounded-full border border-white/[0.12] bg-white/[0.02] text-liftile-cream/80 hover:text-white hover:border-liftile-orange hover:-translate-y-0.5 transition-all duration-400"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                    style={{
                      background:
                        "linear-gradient(140deg, #F97316 0%, #FF6B1A 100%)",
                    }}
                  />
                  <Icon className="relative w-[15px] h-[15px]" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Quick Links (2 cols) ─── */}
        <nav aria-label="Quick links" className="lg:col-span-2">
          <ColumnHeading>Quick Links</ColumnHeading>
          <ul className="space-y-3">
            {QUICK_LINKS.map(([label, to]) => (
              <li key={label}>
                <Link
                  to={to}
                  className="group inline-flex items-center gap-1.5 text-[13.5px] text-liftile-cream/60 hover:text-liftile-orange transition-colors duration-300"
                >
                  <span
                    aria-hidden
                    className="w-0 group-hover:w-3 h-px bg-liftile-orange transition-all duration-300"
                  />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* ─── Products (3 cols) ─── */}
        <nav aria-label="Our products" className="lg:col-span-3">
          <ColumnHeading>Our Products</ColumnHeading>
          <ul className="space-y-3">
            {PRODUCTS.slice(0, 7).map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/products/${p.slug}`}
                  className="group inline-flex items-center gap-1.5 text-[13.5px] text-liftile-cream/60 hover:text-liftile-orange transition-colors duration-300"
                >
                  <span
                    aria-hidden
                    className="w-0 group-hover:w-3 h-px bg-liftile-orange transition-all duration-300"
                  />
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* ─── Contact Information (3 cols) ─── */}
        <div className="lg:col-span-3">
          <ColumnHeading>Contact Information</ColumnHeading>
          <ul className="space-y-5">
            <li className="flex gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 border border-liftile-orange/40" style={{ backgroundColor: "rgba(249,115,22,0.10)" }}>
                <MapPin className="w-3.5 h-3.5 text-liftile-orange" strokeWidth={2.2} />
              </span>
              <span className="font-body text-[13px] text-liftile-cream/65 leading-relaxed pt-1">
                {BRAND.address}
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 border border-liftile-orange/40" style={{ backgroundColor: "rgba(249,115,22,0.10)" }}>
                <Phone className="w-3.5 h-3.5 text-liftile-orange" strokeWidth={2.2} />
              </span>
              <a
                href={`tel:${BRAND.phone}`}
                data-testid="footer-phone"
                className="font-body text-[13.5px] text-liftile-cream/80 hover:text-liftile-orange transition-colors duration-300"
              >
                {BRAND.phone}
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 border border-liftile-orange/40" style={{ backgroundColor: "rgba(249,115,22,0.10)" }}>
                <Mail className="w-3.5 h-3.5 text-liftile-orange" strokeWidth={2.2} />
              </span>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="font-body text-[13.5px] text-liftile-cream/80 hover:text-liftile-orange transition-colors duration-300 break-all"
              >
                {BRAND.email}
              </a>
            </li>
          </ul>

          {/* Mini "Get a Quote" prompt */}
          <Link
            to="/contact"
            data-testid="footer-quote-cta"
            className="group inline-flex items-center gap-2 mt-7 pl-4 pr-3 py-2.5 rounded-full border border-liftile-orange/45 text-liftile-orange font-heading uppercase tracking-[0.18em] text-[10.5px] font-semibold hover:bg-liftile-orange hover:text-white transition-all duration-400"
          >
            Get a Quote
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>

      {/* ═══════════ Certification strip ═══════════ */}
      <div className="border-t border-white/[0.06] bg-black/25">
        <div className="container-x py-6 flex flex-col md:flex-row items-center md:items-center justify-center md:justify-center gap-4 md:gap-6">
          <span className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-cream/45 font-semibold">
            Certifications & Country of Origin
          </span>
          <span aria-hidden className="hidden md:inline-block w-6 h-px bg-white/[0.12]" />
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <MadeInIndiaBadge />
            <ISOCertifiedBadge />
            <div
              data-testid="footer-owner-inspected"
              className="inline-flex items-center gap-2.5 pl-2.5 pr-4 py-2 rounded-full border border-white/[0.08] bg-white/[0.02] hover:border-liftile-orange/40 hover:bg-white/[0.04] transition-all duration-400"
            >
              <span
                aria-hidden
                className="flex items-center justify-center w-9 h-9 rounded-full shadow-[0_4px_14px_-4px_rgba(249,115,22,0.4)]"
                style={{
                  background:
                    "linear-gradient(140deg, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.06) 100%)",
                  border: "1px solid rgba(249,115,22,0.45)",
                }}
              >
                <Award className="w-4 h-4 text-liftile-orange" strokeWidth={2.4} />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-heading text-[10px] uppercase tracking-[0.22em] text-liftile-cream/55 font-semibold">
                  Every Machine
                </span>
                <span className="font-heading text-[13px] uppercase tracking-[0.14em] text-liftile-cream font-bold">
                  Owner Inspected
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ Bottom copyright strip ═══════════ */}
      <div className="border-t border-white/[0.06]">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="font-body text-[11.5px] text-liftile-cream/70 tracking-wide">
            © {new Date().getFullYear()} Liftile Machinery Pvt. Ltd. All Rights Reserved.
          </span>
          <div className="flex flex-col items-center md:items-end gap-2">
            <div className="flex items-center gap-6">
              <a
                href="#"
                className="font-heading text-[10.5px] uppercase tracking-[0.22em] text-liftile-cream/70 hover:text-liftile-orange transition-colors duration-300"
              >
                Privacy Policy
              </a>
              <span aria-hidden className="text-liftile-cream/40">|</span>
              <a
                href="#"
                className="font-heading text-[10.5px] uppercase tracking-[0.22em] text-liftile-cream/70 hover:text-liftile-orange transition-colors duration-300"
              >
                Terms &amp; Conditions
              </a>
            </div>
            <span
              data-testid="footer-version-text"
              className="font-body text-[10.5px] text-liftile-cream/60 tracking-wide"
            >
              Website Version: LIFTILE 2.0 <span className="text-liftile-cream/40 mx-1">|</span> Version 3.0 Coming Soon
            </span>
          </div>
        </div>
      </div>

      {/* Component-scoped keyframes */}
      <style>{`
        @keyframes footer-logo-breathe {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.95; transform: scale(1.04); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="footer-logo-breathe"] { animation: none !important; }
        }
      `}</style>
    </footer>
  );
}
